import React, { useState } from "react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { useOfferGroup, useUpdateOfferGroup, useDeleteOfferGroup } from "@/hooks/use-offer-groups";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/Card";
import { Trash2, Megaphone } from "lucide-react";

export function OfferGroupDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const { data, isLoading } = useOfferGroup(id!);
  const updateMutation = useUpdateOfferGroup(id!);
  const deleteMutation = useDeleteOfferGroup();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");

  const group = data?.data;
  const memberOffers = group?.offers ?? [];

  function startEdit() {
    setName(group.name);
    setStatus(group.status);
    setEditing(true);
  }

  async function handleSave() {
    await updateMutation.mutateAsync({ name, status });
    setEditing(false);
  }

  async function handleDelete() {
    if (!group) return;
    if (!window.confirm(`Delete offer group "${group.name}"? All offers will be unlinked.`)) return;
    await deleteMutation.mutateAsync(group.id);
    navigate({ to: "/offer-groups" });
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Loading..." backHref="/offer-groups" />
        <div className="animate-pulse space-y-4">
          <div className="h-48 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div>
        <PageHeader title="Group Not Found" backHref="/offer-groups" />
        <p className="text-gray-500">This offer group does not exist.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <PageHeader
          title={group.name}
          description={`Offer Group #${group.id}`}
          backHref="/offer-groups"
        />
        <div className="flex gap-2">
          {!editing && (
            <button
              onClick={startEdit}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Edit
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Details">
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Status</dt>
                  <dd className="mt-1"><StatusBadge status={group.status} /></dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Caps</dt>
                  <dd className="mt-1 text-sm text-gray-700">{group.isCapsEnabled ? "Enabled" : "Disabled"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase">Created</dt>
                  <dd className="mt-1 text-sm text-gray-700">{new Date(group.createdAt).toLocaleDateString()}</dd>
                </div>
                {group.advertiserId && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500 uppercase">Advertiser ID</dt>
                    <dd className="mt-1 text-sm text-gray-700">#{group.advertiserId}</dd>
                  </div>
                )}
              </dl>
            )}
          </Card>

          <Card title={`Member Offers (${memberOffers.length})`}>
            {memberOffers.length === 0 ? (
              <p className="text-sm text-gray-500">
                No offers are assigned to this group yet. Set the offer group when creating or editing an offer.
              </p>
            ) : (
              <div className="space-y-2">
                {memberOffers.map((o: any) => (
                  <Link
                    key={o.id}
                    to="/offers/$id"
                    params={{ id: String(o.id) }}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <Megaphone className="w-4 h-4 text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-900 flex-1">{o.name}</span>
                    <StatusBadge status={o.status} />
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>

        {group.isCapsEnabled && (
          <div>
            <Card title="Caps">
              <dl className="space-y-3">
                {group.dailyConversionCap > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Daily Conv.</span>
                    <span className="font-medium">{group.dailyConversionCap}</span>
                  </div>
                )}
                {group.weeklyConversionCap > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Weekly Conv.</span>
                    <span className="font-medium">{group.weeklyConversionCap}</span>
                  </div>
                )}
                {group.monthlyConversionCap > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Monthly Conv.</span>
                    <span className="font-medium">{group.monthlyConversionCap}</span>
                  </div>
                )}
                {group.globalConversionCap > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Global Conv.</span>
                    <span className="font-medium">{group.globalConversionCap}</span>
                  </div>
                )}
              </dl>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
